/**
 * ComponentTreeView - Дерево ієрархії компонентів з підсвічуванням зв'язків
 */

import {
    AppstoreOutlined,
    ClockCircleOutlined,
    DatabaseOutlined,
    FileOutlined,
    HomeOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons'
import type { TreeDataNode } from 'antd'
import { Badge, Breadcrumb, Input, Space, Tag, Tooltip, Tree, Typography } from 'antd'
import React, { useMemo, useState } from 'react'
import type { ProfilerComponentInfo } from '../../../Managers/Profiler/ProfilerManager'

const { Text } = Typography
const { Search } = Input

interface ComponentTreeViewProps {
    components: ProfilerComponentInfo[]
    onSelectComponent?: (componentId: string) => void
    selectedComponentId?: string
}

/**
 * Компонент дерева ієрархії компонентів
 */
export const ComponentTreeView: React.FC<ComponentTreeViewProps> = ({
    components,
    onSelectComponent,
    selectedComponentId,
}) => {
    const [expandedKeys, setExpandedKeys] = useState<string[]>([])
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)
    const [searchValue, setSearchValue] = useState('')
    const [autoExpandParent, setAutoExpandParent] = useState(true)

    // Створюємо мапу для швидкого доступу
    const componentMap = useMemo(() => {
        const map = new Map<string, ProfilerComponentInfo>()
        components.forEach((comp) => map.set(comp.id, comp))
        return map
    }, [components])

    // Знаходимо кореневі компоненти (без батьків)
    const rootComponents = useMemo(() => {
        return components.filter((comp) => !comp.parentId || !componentMap.has(comp.parentId))
    }, [components, componentMap])

    // Отримуємо всіх батьків та дітей для підсвічування
    const getRelatedIds = (componentId: string): Set<string> => {
        const related = new Set<string>()
        const component = componentMap.get(componentId)
        if (!component) return related

        // Додаємо сам компонент
        related.add(componentId)

        // Додаємо всіх батьків (вгору по дереву)
        let currentId: string | undefined = component.parentId
        while (currentId) {
            related.add(currentId)
            const parent = componentMap.get(currentId)
            currentId = parent?.parentId
        }

        // Додаємо всіх дітей (вниз по дереву)
        const addChildren = (id: string) => {
            const comp = componentMap.get(id)
            if (!comp) return
            comp.children.forEach((childId) => {
                related.add(childId)
                addChildren(childId)
            })
        }
        addChildren(componentId)

        return related
    }

    // Отримуємо шлях breadcrumb для компонента
    const getBreadcrumbPath = (componentId: string): ProfilerComponentInfo[] => {
        const path: ProfilerComponentInfo[] = []
        let currentId: string | undefined = componentId

        while (currentId) {
            const component = componentMap.get(currentId)
            if (!component) break
            path.unshift(component)
            currentId = component.parentId
        }

        return path
    }

    // Рекурсивно будуємо дерево
    const buildTreeData = (comp: ProfilerComponentInfo, relatedIds: Set<string>): TreeDataNode => {
        const isHovered = hoveredKey === comp.id
        const isRelated = relatedIds.has(comp.id)
        const isSelected = selectedComponentId === comp.id
        const isError = comp.status === 'error'
        const isSlow = comp.averageRenderTime > 50

        // Підсвічування для зв'язаних компонентів
        const titleStyle: React.CSSProperties = {
            padding: '2px 8px',
            borderRadius: '4px',
            transition: 'all 0.3s',
            backgroundColor: isSelected
                ? '#e6f7ff'
                : isHovered
                ? '#f0f0f0'
                : isRelated
                ? '#fff7e6'
                : 'transparent',
            border: isSelected ? '1px solid #1890ff' : '1px solid transparent',
        }

        const title = (
            <div
                style={titleStyle}
                onMouseEnter={() => setHoveredKey(comp.id)}
                onMouseLeave={() => setHoveredKey(null)}
            >
                <Space size={4}>
                    {comp.type === 'component' ? (
                        <AppstoreOutlined style={{ color: '#1890ff' }} />
                    ) : (
                        <FileOutlined style={{ color: '#722ed1' }} />
                    )}
                    <Text strong={isSelected}>{comp.name}</Text>
                    {isError && <Badge status="error" />}
                    {isSlow && (
                        <Tooltip title={`Повільно: ${comp.averageRenderTime.toFixed(1)}ms`}>
                            <Tag color="warning" style={{ fontSize: '10px', padding: '0 4px' }}>
                                Slow
                            </Tag>
                        </Tooltip>
                    )}
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                        ({comp.renderCount})
                    </Text>
                </Space>
            </div>
        )

        const children = comp.children
            .map((childId) => componentMap.get(childId))
            .filter((child): child is ProfilerComponentInfo => !!child)
            .map((child) => buildTreeData(child, relatedIds))

        return {
            key: comp.id,
            title,
            children: children.length > 0 ? children : undefined,
            icon: null,
        }
    }

    // Пошук по дереву
    const getMatchedKeys = (searchVal: string): string[] => {
        if (!searchVal) return []

        const matched: string[] = []
        const searchLower = searchVal.toLowerCase()

        components.forEach((comp) => {
            if (
                comp.name.toLowerCase().includes(searchLower) ||
                comp.id.toLowerCase().includes(searchLower) ||
                comp.path.toLowerCase().includes(searchLower)
            ) {
                matched.push(comp.id)
                // Додаємо всіх батьків для розгортання
                let parentId = comp.parentId
                while (parentId) {
                    if (!matched.includes(parentId)) {
                        matched.push(parentId)
                    }
                    const parent = componentMap.get(parentId)
                    parentId = parent?.parentId
                }
            }
        })

        return matched
    }

    const handleSearch = (value: string) => {
        setSearchValue(value)
        if (value) {
            const matched = getMatchedKeys(value)
            setExpandedKeys(matched)
            setAutoExpandParent(true)
        } else {
            setExpandedKeys([])
            setAutoExpandParent(false)
        }
    }

    // Отримуємо зв'язані ID для підсвічування
    const relatedIds = useMemo(() => {
        return hoveredKey ? getRelatedIds(hoveredKey) : new Set<string>()
    }, [hoveredKey, componentMap])

    // Будуємо дерево даних
    const treeData = useMemo(() => {
        return rootComponents.map((comp) => buildTreeData(comp, relatedIds))
    }, [rootComponents, relatedIds, hoveredKey, selectedComponentId, searchValue])

    // Breadcrumb для вибраного компонента
    const breadcrumbPath = useMemo(() => {
        return selectedComponentId ? getBreadcrumbPath(selectedComponentId) : []
    }, [selectedComponentId, componentMap])

    const selectedComponent = selectedComponentId
        ? componentMap.get(selectedComponentId)
        : undefined

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Пошук */}
            <div style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Пошук компонентів..."
                    onChange={(e) => handleSearch(e.target.value)}
                    allowClear
                    style={{ marginBottom: 8 }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    Наведіть на компонент для підсвічування зв'язків
                </Text>
            </div>

            {/* Breadcrumb */}
            {breadcrumbPath.length > 0 && (
                <div
                    style={{
                        marginBottom: 16,
                        padding: 12,
                        background: '#fafafa',
                        borderRadius: 4,
                        border: '1px solid #d9d9d9',
                    }}
                >
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <HomeOutlined />
                        </Breadcrumb.Item>
                        {breadcrumbPath.map((comp, index) => (
                            <Breadcrumb.Item
                                key={comp.id}
                                onClick={() => onSelectComponent?.(comp.id)}
                            >
                                <Space size={4} style={{ cursor: 'pointer', fontWeight: index === breadcrumbPath.length - 1 ? 'bold' : 'normal' }}>
                                    {comp.type === 'component' ? (
                                        <AppstoreOutlined />
                                    ) : (
                                        <FileOutlined />
                                    )}
                                    {comp.name}
                                </Space>
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>

                    {/* Статистика вибраного компонента */}
                    {selectedComponent && (
                        <div style={{ marginTop: 8 }}>
                            <Space size={16}>
                                <Tooltip title="Рендерів">
                                    <Space size={4}>
                                        <ThunderboltOutlined style={{ color: '#1890ff' }} />
                                        <Text style={{ fontSize: '12px' }}>
                                            {selectedComponent.renderCount}
                                        </Text>
                                    </Space>
                                </Tooltip>
                                <Tooltip title="Середній час">
                                    <Space size={4}>
                                        <ClockCircleOutlined
                                            style={{
                                                color:
                                                    selectedComponent.averageRenderTime > 50
                                                        ? '#ff4d4f'
                                                        : '#52c41a',
                                            }}
                                        />
                                        <Text style={{ fontSize: '12px' }}>
                                            {selectedComponent.averageRenderTime.toFixed(1)}ms
                                        </Text>
                                    </Space>
                                </Tooltip>
                                <Tooltip title="Пам'ять">
                                    <Space size={4}>
                                        <DatabaseOutlined style={{ color: '#722ed1' }} />
                                        <Text style={{ fontSize: '12px' }}>
                                            {(selectedComponent.memoryUsage / 1024).toFixed(1)}KB
                                        </Text>
                                    </Space>
                                </Tooltip>
                                <Tooltip title="Дітей">
                                    <Space size={4}>
                                        <AppstoreOutlined style={{ color: '#faad14' }} />
                                        <Text style={{ fontSize: '12px' }}>
                                            {selectedComponent.children.length}
                                        </Text>
                                    </Space>
                                </Tooltip>
                            </Space>
                        </div>
                    )}
                </div>
            )}

            {/* Дерево */}
            <div style={{ flex: 1, overflow: 'auto' }}>
                {treeData.length > 0 ? (
                    <Tree
                        treeData={treeData}
                        expandedKeys={expandedKeys}
                        autoExpandParent={autoExpandParent}
                        onExpand={(keys) => {
                            setExpandedKeys(keys as string[])
                            setAutoExpandParent(false)
                        }}
                        onSelect={(keys) => {
                            if (keys.length > 0) {
                                onSelectComponent?.(keys[0] as string)
                            }
                        }}
                        selectedKeys={selectedComponentId ? [selectedComponentId] : []}
                        showLine={{ showLeafIcon: false }}
                        showIcon={false}
                        defaultExpandAll={false}
                        height={400}
                        virtual
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                        <AppstoreOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <div>Немає компонентів для відображення</div>
                    </div>
                )}
            </div>

            {/* Легенда */}
            <div
                style={{
                    marginTop: 16,
                    padding: 8,
                    background: '#fafafa',
                    borderRadius: 4,
                    fontSize: '11px',
                }}
            >
                <Space size={16}>
                    <Space size={4}>
                        <div
                            style={{
                                width: 12,
                                height: 12,
                                background: '#e6f7ff',
                                border: '1px solid #1890ff',
                                borderRadius: 2,
                            }}
                        />
                        <Text type="secondary">Вибраний</Text>
                    </Space>
                    <Space size={4}>
                        <div
                            style={{
                                width: 12,
                                height: 12,
                                background: '#fff7e6',
                                borderRadius: 2,
                            }}
                        />
                        <Text type="secondary">Зв'язаний</Text>
                    </Space>
                    <Space size={4}>
                        <Tag color="warning" style={{ fontSize: '10px', margin: 0 }}>
                            Slow
                        </Tag>
                        <Text type="secondary">&gt;50ms</Text>
                    </Space>
                </Space>
            </div>
        </div>
    )
}
